import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DatePicker } from 'rsuite';
import { ArrowLeft, Paperclip, X as XIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cotizacionesAPI, configuracionAPI, clientesAPI, prefijosAPI } from '../services/api';
import HiloVersionesModal from './HiloVersionesModal';
import AnexosSection from './AnexosSection';
import { Download, Eye, FileStack } from 'lucide-react';
import { getCachedClientes } from '../services/cachedCatalogs';
import ClienteAcciones from './ClienteAcciones';
import { moneyFmt, safeId, getCurrentDate } from '../utils/helpers';
import { selectStyles } from '../utils/adminShared';
import { Card, Button, Input, Tooltip, PhoneInput } from './ui';

// Formatea un objeto Date a "YYYY-MM-DD" usando la ZONA LOCAL (no UTC).
// toISOString() convierte a UTC y desplaza el dia en zonas GMT- (Peru = GMT-5).
const dateToYmdLocal = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};


/**
 * Form MANUAL de cotizacion — flujo simplificado:
 *   - Cliente (nombre libre o seleccion de existente)
 *   - Moneda (PEN / USD)
 *   - Incluye IGV (Si / No)
 *   - Monto base ingresado a mano
 *   - Rentabilidad aplicada (default global)
 *
 * Calculo: subtotal = monto_base * (1 + rentabilidad); igv = subtotal * igv_rate (si aplica);
 * total = subtotal + igv.
 *
 * El backend guarda `es_manual=TRUE` + `monto_base` y no crea servicios/personal.
 * El PDF y demas vistas reutilizan la misma cotizacion — el numero, prefijo, moneda,
 * subtotal, igv y total se leen igual que una cotizacion normal.
 */
const CotizacionFormManual = ({ cotizacionId = null }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEditing = !!cotizacionId;
    // Estado del archivo YA guardado en BD (si viene de edicion).
    const [archivoActual, setArchivoActual] = useState(null); // {archivo_path, archivo_nombre}

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [numero, setNumero] = useState('');
    const [configuracion, setConfiguracion] = useState({});
    const [prefijosActivos, setPrefijosActivos] = useState([]);
    const [prefijoSeleccionado, setPrefijoSeleccionado] = useState(null);
    const [clientesList, setClientesList] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [archivo, setArchivo] = useState(null); // File del input (nuevo o reemplazo)
    const [dragActive, setDragActive] = useState(false);
    const [versionesCount, setVersionesCount] = useState(0);
    const [showHiloModal, setShowHiloModal] = useState(false);

    const [form, setForm] = useState({
        cliente_nombre: '',
        cliente_email: '',
        cliente_empresa: '',
        cliente_telefono: '',
        moneda: 'PEN',
        incluye_igv: true,
        monto_base: '', // TOTAL FINAL ingresado
        rentabilidad: '', // segun tipo: % o monto fijo (informativo)
        rentabilidad_tipo: 'porcentaje', // 'porcentaje' | 'fijo'
        fecha: getCurrentDate(),
        numero_manual: '',
        comentarios: ''
    });

    // Cargar configuracion + prefijos manuales + clientes.
    // Si isEditing → cargar la cotizacion existente y precargar el form.
    useEffect(() => {
        (async () => {
            try {
                const [cfgRes, prefRes, cliRes] = await Promise.all([
                    configuracionAPI.getAll(),
                    prefijosAPI.getActivos('manual'),
                    getCachedClientes().catch(() => ({ data: [] }))
                ]);
                const cfg = cfgRes.data || {};
                setConfiguracion(cfg);

                const lista = prefRes?.data || [];
                setPrefijosActivos(lista);
                setClientesList(cliRes.data || []);

                if (isEditing) {
                    // Precargar datos de la cotizacion existente.
                    const cotRes = await cotizacionesAPI.getById(cotizacionId);
                    const c = cotRes.data;
                    // Guard: solo se edita en 'borrador'. Cualquier otro estado va a detalle
                    // (mismo comportamiento que el form normal). Estado se cambia desde
                    // kanban/listado, no desde el editor.
                    if (c.estado && c.estado !== 'borrador') {
                        navigate(`/cotizaciones/${cotizacionId}/detalle`, { replace: true });
                        return;
                    }
                    setNumero(c.numero);
                    setPrefijoSeleccionado(c.prefijo_id || null);
                    setVersionesCount(c.versiones_count || 1);
                    if (c.archivo_path) {
                        setArchivoActual({ archivo_path: c.archivo_path, archivo_nombre: c.archivo_nombre });
                    }
                    // Extraer el correlativo del numero completo para pre-llenar el input editable.
                    // Ej: numero="MPW-5" y prefijo="MPW" → correlativo="5".
                    let correlativoEditable = '';
                    if (c.prefijo_str && c.numero?.startsWith(`${c.prefijo_str}-`)) {
                        correlativoEditable = c.numero.slice(c.prefijo_str.length + 1);
                    }
                    setForm({
                        cliente_nombre: c.cliente_nombre || '',
                        cliente_email: c.cliente_email || '',
                        cliente_empresa: c.cliente_empresa || '',
                        cliente_telefono: c.cliente_telefono || '',
                        moneda: c.moneda || 'PEN',
                        incluye_igv: c.incluye_igv !== 0,
                        monto_base: c.total ? String(c.total) : '', // total final
                        rentabilidad: String(Math.round(parseFloat(cfg.rentabilidad || '0.68') * 100)),
                        fecha: c.fecha?.split('T')[0] || getCurrentDate(),
                        numero_manual: correlativoEditable,
                        comentarios: c.comentarios || ''
                    });
                } else {
                    // Modo crear: elegir prefijo default y traer numero preview.
                    if (!form.rentabilidad) {
                        setForm(f => ({ ...f, rentabilidad: String(Math.round((parseFloat(cfg.rentabilidad || '0.68')) * 100)) }));
                    }
                    const def = lista.find(p => p.es_default) || lista[0];
                    let prefIdInicial = null;
                    if (def) {
                        prefIdInicial = def.id;
                        setPrefijoSeleccionado(def.id);
                    }
                    const numRes = await configuracionAPI.getNextCotizacionNumber(prefIdInicial);
                    setNumero(numRes.data.numero);
                    // Pre-llenar el correlativo editable con el proximo numero automatico.
                    if (numRes.data.consecutivo != null) {
                        setForm(f => ({ ...f, numero_manual: String(numRes.data.consecutivo) }));
                    }
                }
            } catch (err) {
                console.error('[CotizacionFormManual] Error al cargar datos:', err);
                const status = err?.status;
                const msg = err?.message || '';
                if (status === 404 || msg.toLowerCase().includes('no encontrada') || msg.toLowerCase().includes('not found')) {
                    navigate('/404', { replace: true });
                } else {
                    toast.error(`Error al cargar datos: ${err?.message || err}`);
                }
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacionId]);

    const igvRate = useMemo(() => parseFloat(configuracion.igv_rate || '0.18'), [configuracion]);

    const handleCambiarPrefijo = async (id) => {
        setPrefijoSeleccionado(id);
        try {
            const res = await configuracionAPI.getNextCotizacionNumber(id);
            setNumero(res.data.numero);
        } catch (e) {}
    };

    // Cambia de moneda y CONVIERTE el monto base al nuevo tipo de cambio.
    // PEN → USD: base / tc.  USD → PEN: base * tc.
    const handleCambiarMoneda = (nuevaMoneda) => {
        if (nuevaMoneda === form.moneda) return;
        const tc = parseFloat(configuracion.tipo_cambio || '3.70');
        const base = parseFloat(form.monto_base);
        let nuevoBase = form.monto_base;
        if (!isNaN(base) && base > 0 && tc > 0) {
            const convertido = nuevaMoneda === 'USD' ? base / tc : base * tc;
            nuevoBase = convertido.toFixed(2);
        }
        setForm(f => ({ ...f, moneda: nuevaMoneda, monto_base: nuevoBase }));
    };

    // Calculos en vivo — TODO INVERSO (el monto ingresado es el TOTAL FINAL).
    // - Con IGV: subtotal = total / (1 + rate);  igv = total - subtotal.
    // - Sin IGV: subtotal = total; igv = 0.
    // Rentabilidad informativa (dos modos):
    //   - porcentaje: costo = subtotal / (1 + rent%),  ganancia = subtotal - costo
    //   - fijo: ganancia = monto_fijo,  costo = subtotal - ganancia
    const computed = useMemo(() => {
        const total = parseFloat(form.monto_base) || 0;
        let subtotal, igv;
        if (form.incluye_igv) {
            subtotal = total / (1 + igvRate);
            igv = total - subtotal;
        } else {
            subtotal = total;
            igv = 0;
        }

        const rentVal = parseFloat(form.rentabilidad);
        let costo, ganancia;
        if (form.rentabilidad_tipo === 'fijo') {
            ganancia = !isNaN(rentVal) ? rentVal : 0;
            costo = subtotal - ganancia;
        } else {
            const rent = !isNaN(rentVal) ? rentVal / 100 : parseFloat(configuracion.rentabilidad || '0.68');
            costo = subtotal / (1 + rent);
            ganancia = subtotal - costo;
        }

        // Equivalente en la otra moneda usando tipo de cambio global.
        const tc = parseFloat(configuracion.tipo_cambio || '3.70');
        const otherCurrency = form.moneda === 'PEN' ? 'USD' : 'PEN';
        const totalOther = form.moneda === 'PEN' ? (tc > 0 ? total / tc : 0) : total * tc;
        return { costo, ganancia, subtotal, igv, total, totalOther, otherCurrency };
    }, [form.monto_base, form.rentabilidad, form.rentabilidad_tipo, form.incluye_igv, form.moneda, igvRate, configuracion]);

    const seleccionarCliente = (opt) => {
        if (!opt) {
            setClienteSeleccionado(null);
            return;
        }
        const c = opt.data;
        setClienteSeleccionado(opt);
        setForm(f => ({
            ...f,
            cliente_nombre: c.nombre || '',
            cliente_email: c.email || '',
            cliente_empresa: c.empresa || '',
            cliente_telefono: c.telefono || ''
        }));
    };

    const handleGuardar = async () => {
        if (!form.cliente_nombre.trim()) {
            toast.error('El nombre del cliente es obligatorio');
            return;
        }
        const base = parseFloat(form.monto_base);
        if (isNaN(base) || base <= 0) {
            toast.error('Ingresa un monto base válido');
            return;
        }
        // En edicion, solo obligatorio si NO hay archivo previo.
        if (!archivo && !archivoActual) {
            toast.error('El archivo adjunto es obligatorio para cotizaciones manuales');
            return;
        }
        setSaving(true);
        try {
            // Asociar cliente igual que el form normal
            let cliente_id = null;
            const existente = clientesList.find(c =>
                (c.nombre || '').toLowerCase() === form.cliente_nombre.toLowerCase() &&
                (c.empresa || '').toLowerCase() === form.cliente_empresa.toLowerCase() &&
                (c.email || '').toLowerCase() === form.cliente_email.toLowerCase()
            );
            if (existente) {
                cliente_id = existente.id;
            } else {
                try {
                    const clienteRes = await clientesAPI.create({
                        nombre: form.cliente_nombre.trim(),
                        empresa: form.cliente_empresa.trim(),
                        email: form.cliente_email.trim(),
                        telefono: form.cliente_telefono.trim()
                    });
                    cliente_id = clienteRes.data.id;
                } catch (e) {}
            }

            const payload = {
                cliente_id,
                cliente_nombre: form.cliente_nombre.trim(),
                cliente_email: form.cliente_email.trim(),
                cliente_empresa: form.cliente_empresa.trim(),
                cliente_telefono: form.cliente_telefono.trim(),
                moneda: form.moneda,
                incluye_igv: form.incluye_igv,
                comentarios: form.comentarios,
                prefijo_id: prefijoSeleccionado,
                asignados_ids: user?.id ? [user.id] : [],
                es_manual: true,
                monto_base: base, // TOTAL FINAL (IGV backwards se calcula server-side)
                fecha: form.fecha || getCurrentDate(),
                servicios: [],
                extras: []
            };

            let cotIdFinal;
            let numeroFinal;
            if (isEditing) {
                await cotizacionesAPI.update(cotizacionId, payload);
                cotIdFinal = cotizacionId;
                numeroFinal = numero;
            } else {
                // Solo en creacion: enviar numero_manual si el user edito el correlativo.
                if (form.numero_manual && String(form.numero_manual).trim()) {
                    payload.numero_manual = String(form.numero_manual).trim();
                }
                const res = await cotizacionesAPI.create(payload);
                cotIdFinal = res.data.id;
                numeroFinal = res.data.numero;
            }

            // Subir archivo nuevo si se seleccionó uno (crear o reemplazar).
            if (archivo && cotIdFinal) {
                try {
                    await cotizacionesAPI.subirArchivo(cotIdFinal, archivo);
                } catch (uploadErr) {
                    console.error('[CotizacionFormManual] Error subiendo archivo:', uploadErr);
                    toast.error(`Cotización ${isEditing ? 'actualizada' : 'creada'}, pero el archivo no se subió: ${uploadErr.message}`);
                }
            }

            toast.success(`Cotización ${isEditing ? 'actualizada' : 'creada'}: ${numeroFinal}`);
            navigate('/cotizaciones');
        } catch (err) {
            toast.error(err.message || 'No pudimos guardar la cotización');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading">Cargando…</div>;

    // Sin prefijos manuales configurados: mostrar empty state con CTA al panel.
    if (prefijosActivos.length === 0) {
        return (
            <div className="cotizacion-form-manual">
                <div className="flex items-center gap-2 mb-3">
                    <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
                        Volver
                    </Button>
                    <h2 style={{ margin: 0 }}>Nueva cotización manual</h2>
                </div>
                <Card>
                    <Card.Body>
                        <div style={{ padding: 24, textAlign: 'center' }}>
                            <h3 className="h3" style={{ marginBottom: 8 }}>No hay prefijos manuales configurados</h3>
                            <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
                                Para crear cotizaciones manuales necesitas al menos un prefijo con tipo <strong>Manual</strong>.
                                Créalo desde el panel de Numeraciones.
                            </p>
                            <Button variant="primary" onClick={() => navigate('/numeraciones')}>
                                Ir a Numeraciones
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="cotizacion-form-manual">
            <div className="flex items-center gap-2 mb-3">
                <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
                    Volver
                </Button>
                <h2 style={{ margin: 0 }}>
                    {isEditing ? `Editar cotización manual · ${numero}` : `Nueva cotización manual · ${numero}`}
                </h2>
                {isEditing && versionesCount > 1 && (
                    <button
                        type="button"
                        className="versiones-badge"
                        onClick={() => setShowHiloModal(true)}
                        aria-label={`Ver hilo de versiones (${versionesCount})`}
                    >
                        <FileStack size={13} aria-hidden="true" />
                        {versionesCount}
                    </button>
                )}
            </div>

            {prefijosActivos.length >= 1 && (
                <Card className="prefijo-selector-card">
                    <Card.Body>
                        <div className="prefijo-selector-row">
                            <div className="prefijo-selector-info">
                                <span className="label">Numeración</span>
                                <p className="text-xs text-muted">
                                    {prefijosActivos.length === 1 ? 'Prefijo manual asignado.' : 'Elige el prefijo manual.'}
                                </p>
                            </div>
                            <div className="prefijo-selector-buttons" role="group">
                                {prefijosActivos.map(p => {
                                    const activo = prefijoSeleccionado === p.id;
                                    const tooltipContent = (
                                        <div style={{ maxWidth: 220, lineHeight: 1.4 }}>
                                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{p.nombre}</div>
                                            {p.descripcion && (
                                                <div style={{ fontSize: 12, opacity: 0.85, marginBottom: p.tipo ? 4 : 0 }}>
                                                    {p.descripcion}
                                                </div>
                                            )}
                                            {p.tipo && (
                                                <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                    {p.tipo === 'manual' ? 'Histórico / Manual' : 'Normal'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                    return (
                                        <Tooltip key={p.id} content={tooltipContent}>
                                            <button
                                                type="button"
                                                onClick={() => handleCambiarPrefijo(p.id)}
                                                className={`prefijo-chip ${activo ? 'is-active' : ''}`}
                                                style={activo ? { backgroundColor: p.color, borderColor: p.color, color: '#fff' } : { borderColor: p.color, color: p.color }}
                                                aria-pressed={activo}
                                                aria-label={`${p.nombre}${p.descripcion ? ' — ' + p.descripcion : ''}`}
                                            >
                                                {p.prefijo}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            <div className="moneda-igv-row moneda-igv-row-manual">
                <Card className="moneda-card">
                    <Card.Header title="Moneda de la cotización" />
                    <Card.Body className="flex-1 flex flex-col">
                        <div className="moneda-buttons" role="group" aria-label="Moneda de la cotización">
                            <Button
                                variant={form.moneda === 'PEN' ? 'primary' : 'ghost'}
                                onClick={() => handleCambiarMoneda('PEN')}
                                className="moneda-btn"
                                aria-pressed={form.moneda === 'PEN'}
                            >
                                Soles (PEN)
                            </Button>
                            <Button
                                variant={form.moneda === 'USD' ? 'primary' : 'ghost'}
                                onClick={() => handleCambiarMoneda('USD')}
                                className="moneda-btn"
                                aria-pressed={form.moneda === 'USD'}
                            >
                                Dólares (USD)
                            </Button>
                        </div>

                        <div className="moneda-info">
                            <span className="tc-badge">
                                Tipo de cambio: 1 USD = {parseFloat(configuracion.tipo_cambio || '3.70')} PEN
                            </span>
                            {form.incluye_igv && (
                                <span className="igv-badge">IGV: {Math.round(igvRate * 100)}%</span>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                <Card className="igv-card">
                    <Card.Header title="¿Incluye IGV?" />
                    <Card.Body className="flex-1 flex flex-col">
                        <div className="igv-toggle-buttons" role="group" aria-label="Incluir IGV">
                            <Button
                                variant={form.incluye_igv ? 'primary' : 'ghost'}
                                onClick={() => setForm(f => ({ ...f, incluye_igv: true }))}
                                className="igv-toggle-btn"
                                aria-pressed={form.incluye_igv}
                            >
                                Sí ({Math.round(igvRate * 100)}%)
                            </Button>
                            <Button
                                variant={!form.incluye_igv ? 'primary' : 'ghost'}
                                onClick={() => setForm(f => ({ ...f, incluye_igv: false }))}
                                className="igv-toggle-btn"
                                aria-pressed={!form.incluye_igv}
                            >
                                No
                            </Button>
                        </div>
                        <p className="igv-hint">
                            {form.incluye_igv
                                ? `El total incluye IGV del ${Math.round(igvRate * 100)}%.`
                                : 'El total NO incluye IGV. Se guardará como 0.'}
                        </p>
                    </Card.Body>
                </Card>
            </div>

            {/* Numeracion editable + fecha del documento historico. */}
            <Card className="mt-3.5">
                <Card.Header
                    title="Numeración y fecha"
                    subtitle="Puedes editar el correlativo (útil para migrar cotizaciones antiguas) y elegir la fecha real del documento."
                />
                <Card.Body>
                    <div className="manual-numero-fecha-row">
                        <div>
                            {(() => {
                                const prefStr = prefijosActivos.find(p => p.id === prefijoSeleccionado)?.prefijo || 'COT';
                                return (
                                    <Input
                                        label="Número de cotización"
                                        prefix={<span className="numero-prefijo-label">{prefStr}-</span>}
                                        value={form.numero_manual}
                                        onChange={e => setForm(f => ({ ...f, numero_manual: e.target.value }))}
                                        placeholder="Ej: 5, 2023-45, 100A"
                                        hint="Correlativo tras el prefijo. Debe ser único dentro de este prefijo."
                                    />
                                );
                            })()}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">Fecha del documento</label>
                            <DatePicker
                                value={form.fecha ? new Date(form.fecha + 'T00:00:00') : null}
                                onChange={(date) => setForm(f => ({
                                    ...f,
                                    fecha: dateToYmdLocal(date)
                                }))}
                                format="dd/MM/yyyy"
                                oneTap
                                placement="bottomStart"
                                className="cot-form-datepicker"
                                block
                            />
                            <p className="text-xs text-muted">
                                Fecha real en que se emitió el documento histórico.
                            </p>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mt-3.5">
                <Card.Header title="Datos del cliente" />
                <Card.Body>
                    <div className="field mb-3">
                        <span className="label">Buscar cliente existente</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <Select
                                    styles={selectStyles}
                                    options={clientesList.map(c => ({
                                        value: c.id,
                                        label: c.empresa ? `${c.nombre} - ${c.empresa}` : c.nombre,
                                        data: c
                                    }))}
                                    value={clienteSeleccionado}
                                    onChange={seleccionarCliente}
                                    placeholder="Seleccionar cliente..."
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <ClienteAcciones
                                clienteSeleccionado={clienteSeleccionado}
                                onClientesActualizados={setClientesList}
                                onClienteCreado={(nuevo) => seleccionarCliente({
                                    value: nuevo.id,
                                    label: nuevo.empresa ? `${nuevo.nombre} - ${nuevo.empresa}` : nuevo.nombre,
                                    data: nuevo
                                })}
                                onContactoSeleccionado={(c) => setForm(f => ({
                                    ...f,
                                    cliente_nombre: c.nombre || '',
                                    cliente_email: c.email || '',
                                    cliente_telefono: c.telefono || ''
                                }))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input label="Nombre del cliente" value={form.cliente_nombre} onChange={e => setForm(f => ({ ...f, cliente_nombre: e.target.value }))} required />
                        <Input label="Empresa" value={form.cliente_empresa} onChange={e => setForm(f => ({ ...f, cliente_empresa: e.target.value }))} />
                        <Input label="Email" type="email" value={form.cliente_email} onChange={e => setForm(f => ({ ...f, cliente_email: e.target.value }))} />
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                value={form.cliente_telefono}
                                onChange={(v) => setForm(f => ({ ...f, cliente_telefono: v || '' }))}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mt-3.5">
                <Card.Header title="Monto y cálculo" />
                <Card.Body>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input
                            label="Monto total"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.monto_base}
                            onChange={e => setForm(f => ({ ...f, monto_base: e.target.value }))}
                            placeholder="0.00"
                            hint="Total final del documento. El IGV se calcula backwards."
                        />
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">
                                Rentabilidad ({form.rentabilidad_tipo === 'fijo' ? (form.moneda === 'USD' ? '$' : 'S/') : '%'})
                            </label>
                            <div className="rentabilidad-input-wrap">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="input flex-1"
                                    value={form.rentabilidad}
                                    onChange={e => setForm(f => ({ ...f, rentabilidad: e.target.value }))}
                                    placeholder={form.rentabilidad_tipo === 'fijo' ? '0.00' : '68'}
                                />
                                <div className="rentabilidad-tipo-toggle" role="group" aria-label="Tipo de rentabilidad">
                                    <Tooltip content="Porcentaje">
                                        <button
                                            type="button"
                                            className={form.rentabilidad_tipo === 'porcentaje' ? 'is-active' : ''}
                                            onClick={() => setForm(f => ({ ...f, rentabilidad_tipo: 'porcentaje' }))}
                                            aria-label="Porcentaje"
                                        >
                                            %
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Monto fijo">
                                        <button
                                            type="button"
                                            className={form.rentabilidad_tipo === 'fijo' ? 'is-active' : ''}
                                            onClick={() => setForm(f => ({ ...f, rentabilidad_tipo: 'fijo' }))}
                                            aria-label="Monto fijo"
                                        >
                                            {form.moneda === 'USD' ? '$' : 'S/'}
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                            <p className="text-xs text-muted">
                                Informativo — {form.rentabilidad_tipo === 'porcentaje'
                                    ? `Default: ${Math.round((parseFloat(configuracion.rentabilidad || '0.68')) * 100)}%`
                                    : 'Monto fijo de ganancia'}
                            </p>
                        </div>
                    </div>

                    <div className="summary-grid mt-4">
                        <div className="summary">
                            {/* Valores INVERSOS calculados backwards desde el total ingresado.
                                Costo y Ganancia son informativos (se derivan del subtotal). */}
                            <div className="sumRow interno-row">
                                <span className="sumLabel">Costo Total</span>
                                <span className="sumValue">{moneyFmt(computed.costo, form.moneda)}</span>
                            </div>
                            <div className="sumRow interno-row">
                                <span className="sumLabel">Ganancia Total</span>
                                <span className="sumValue ganancia-positiva">{moneyFmt(computed.ganancia, form.moneda)}</span>
                            </div>
                            <div className="divider interno-divider" />

                            {/* Valores que van al PDF — con IGV backwards */}
                            <div className="sumRow">
                                <span className="sumLabel">Subtotal</span>
                                <span className="sumValue">{moneyFmt(computed.subtotal, form.moneda)}</span>
                            </div>
                            {form.incluye_igv && (
                                <div className="sumRow">
                                    <span className="sumLabel">IGV ({Math.round(igvRate * 100)}%)</span>
                                    <span className="sumValue">{moneyFmt(computed.igv, form.moneda)}</span>
                                </div>
                            )}
                            <div className="divider" />
                            <div className="sumRow big">
                                <span className="sumLabel">TOTAL</span>
                                <span className="sumValue">{moneyFmt(computed.total, form.moneda)}</span>
                            </div>
                            <div className="pill">
                                Equivale a aprox. {moneyFmt(computed.totalOther, computed.otherCurrency)}
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mt-3.5">
                <Card.Header title="Archivo adjunto *" subtitle="Obligatorio — la cotización manual siempre lleva un documento adjunto (PDF, DOC, Excel, imagen)." />
                <Card.Body>
                    {/* Modo edicion: si ya hay un archivo guardado y NO se seleccionó reemplazo, mostrarlo. */}
                    {isEditing && archivoActual && !archivo ? (
                        <div className="archivo-detalle">
                            <div className="archivo-detalle-info">
                                <strong>{archivoActual.archivo_nombre}</strong>
                                <span className="text-xs text-muted">Archivo actual</span>
                            </div>
                            <div className="archivo-detalle-acciones">
                                <Button
                                    variant="soft" size="sm" leftIcon={<Eye size={14} />}
                                    onClick={async () => {
                                        try { await cotizacionesAPI.verArchivo(cotizacionId); }
                                        catch (e) { toast.error(e.message); }
                                    }}
                                >
                                    Ver
                                </Button>
                                <Button
                                    variant="soft" size="sm" leftIcon={<Download size={14} />}
                                    onClick={async () => {
                                        try { await cotizacionesAPI.descargarArchivo(cotizacionId); }
                                        catch (e) { toast.error(e.message); }
                                    }}
                                >
                                    Descargar
                                </Button>
                                <label className="btn-reemplazar">
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                        onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                                        style={{ display: 'none' }}
                                    />
                                    Reemplazar
                                </label>
                            </div>
                        </div>
                    ) : !archivo ? (
                        <label
                            className={`archivo-uploader ${dragActive ? 'is-drag' : ''}`}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                            onDrop={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                setDragActive(false);
                                const f = e.dataTransfer.files?.[0];
                                if (f) setArchivo(f);
                            }}
                        >
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                                style={{ display: 'none' }}
                            />
                            <div className="archivo-uploader-icon">
                                <Paperclip size={32} />
                            </div>
                            <strong className="archivo-uploader-title">
                                {dragActive ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para elegir'}
                            </strong>
                            <div className="archivo-uploader-hint">
                                PDF · DOC · DOCX · XLS · XLSX · PNG · JPG · WEBP
                            </div>
                            <div className="archivo-uploader-hint">Máximo 15MB</div>
                        </label>
                    ) : (
                        <div className="archivo-seleccionado">
                            <Paperclip size={16} />
                            <span className="archivo-nombre">{archivo.name}</span>
                            <span className="text-xs text-muted">{(archivo.size / 1024).toFixed(1)} KB</span>
                            <button
                                type="button"
                                className="archivo-quitar"
                                onClick={() => setArchivo(null)}
                                aria-label="Quitar archivo"
                            >
                                <XIcon size={14} />
                            </button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="mt-3.5">
                <Card.Header title="Comentarios (opcional)" />
                <Card.Body>
                    <textarea
                        className="input"
                        style={{ minHeight: 80, resize: 'vertical', padding: 10, width: '100%' }}
                        value={form.comentarios}
                        onChange={e => setForm(f => ({ ...f, comentarios: e.target.value }))}
                        placeholder="Notas internas u observaciones para el cliente..."
                    />
                </Card.Body>
            </Card>

            {/* Anexos globales del hilo */}
            <AnexosSection cotizacionId={cotizacionId} />

            <div className="flex justify-end gap-2 mt-4">
                <Button variant="ghost" onClick={() => navigate(-1)}>Cancelar</Button>
                <Button variant="primary" onClick={handleGuardar} disabled={saving}>
                    {saving ? 'Guardando…' : (isEditing ? 'Guardar cambios' : 'Crear cotización')}
                </Button>
            </div>

            {isEditing && (
                <HiloVersionesModal
                    open={showHiloModal}
                    cotizacionId={cotizacionId}
                    onClose={() => setShowHiloModal(false)}
                />
            )}
        </div>
    );
};

export default CotizacionFormManual;
